import React from 'react';
import PropTypes from 'prop-types';
import { Typography, Box } from '@mui/material';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import AssessmentIcon from '@mui/icons-material/Assessment';
import BaseCard from './BaseCard';
import {
  STATUS_TO_BE_LOCATED,
  STATUS_LOCATED,
  STATUS_TO_BE_FIXED,
  FONT_FAMILY
} from '../../../dashboardColors';

const CurrentStatusCard = ({ data, t }) => {
  // Prepare data for bar chart with new status colors
  // Combine 'to_be_fixed' and 'being_repaired' into a single 'to_be_fixed' bar
  const chartData = [
    { name: t('to_be_located'), shortName: t('to_be_located'), value: data?.toBeLocated || 0, color: STATUS_TO_BE_LOCATED },
    { name: t('located'), shortName: t('located'), value: data?.located || 0, color: STATUS_LOCATED },
    { name: t('to_be_fixed'), shortName: t('to_be_fixed'), value: (data?.toBeFixed || 0) + (data?.isRepaired || 0), color: STATUS_TO_BE_FIXED }
  ];

  // Find max value for Y axis with some padding
  const maxValue = Math.max(...chartData.map(d => d.value), 10);
  const yAxisMax = Math.ceil(maxValue * 1.1); // Add 10% padding to top

  return (
    <BaseCard
      title={t('current_status')}
      icon={<AssessmentIcon />}
      className="chart-card"
    >
      <Box display="flex" flexDirection="column" height="100%">
        <Box sx={{ flex: 1, minHeight: 160, mt: 1 }}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart
              data={chartData}
              margin={{ top: 10, right: 10, left: 0, bottom: 22 }}
              barSize={70}
            >
              <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
              <XAxis
                dataKey="name"
                tick={{ fontSize: 14, fill: '#666', fontWeight: 600, fontFamily: FONT_FAMILY }}
                interval={0}
                angle={0}
                textAnchor="middle"
                height={45}
              />
              <YAxis
                domain={[0, yAxisMax]}
                tick={{ fontSize: 14, fill: '#666', fontWeight: 600, fontFamily: FONT_FAMILY }}
                axisLine={{ stroke: '#e0e0e0' }}
                ticks={[0, Math.floor(yAxisMax * 0.25), Math.floor(yAxisMax * 0.5), Math.floor(yAxisMax * 0.75), yAxisMax]}
              />
              <Tooltip
                contentStyle={{ fontSize: 14, fontWeight: 600, fontFamily: FONT_FAMILY }}
                formatter={(value) => [value, t('count') || 'Count']}
                labelFormatter={(label) => {
                  const item = chartData.find(d => d.name === label);
                  return item ? item.shortName : label;
                }}
              />
              <Bar
                dataKey="value"
                radius={[4, 4, 0, 0]}
                shape={(props) => {
                  const { x, y, width, height } = props;
                  const data = props.payload;
                  // Create path for rounded top corners
                  const radius = 4;
                  const path = `
                    M ${x},${y + height}
                    L ${x},${y + radius}
                    Q ${x},${y} ${x + radius},${y}
                    L ${x + width - radius},${y}
                    Q ${x + width},${y} ${x + width},${y + radius}
                    L ${x + width},${y + height}
                    Z
                  `;
                  return (
                    <g>
                      <path d={path} fill={data.color} />
                      {data.value > 0 && (
                        <text
                          x={x + width / 2}
                          y={y - 5}
                          fill={data.color}
                          textAnchor="middle"
                          fontSize="16"
                          fontWeight="700"
                          fontFamily={FONT_FAMILY}
                        >
                          {data.value}
                        </text>
                      )}
                    </g>
                  );
                }}
              />
            </BarChart>
          </ResponsiveContainer>
        </Box>

        <Typography variant="body2" align="left" sx={{ fontStyle: 'italic', fontSize: '0.5rem', color: '#666', mt: 1, px: 1 }}>
          * {t('to_be_fixed_note')}
        </Typography>
      </Box>
    </BaseCard>
  );
};

CurrentStatusCard.propTypes = {
  data: PropTypes.shape({
    toBeLocated: PropTypes.number,
    located: PropTypes.number,
    toBeFixed: PropTypes.number,
    isRepaired: PropTypes.number
  }),
  t: PropTypes.func.isRequired
};

export default CurrentStatusCard;